/**
 * REST API Client — core HTTP layer
 *
 * Shared `fetch` wrapper, error type, and file-download/OpenAPI helpers used
 * by every per-entity module under `lib/apiClient/`. Split out of the former
 * monolithic `lib/apiClient.ts` (issue #1406, part of epic #1402) with no
 * behavior change — see `crudFactory.ts` for the generic create/update/delete
 * factory built on top of `apiRequest` below.
 */

import { store } from '../../store';

export const API_BASE_URL = import.meta.env.VITE_REST_API_URL || 'http://localhost:3001/api/v1';

/**
 * API Error class
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public data?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Make authenticated API request
 */
export async function apiRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;

  const token = store.getState().auth.token;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  // Handle 204 No Content (no response body)
  if (response.status === 204) {
    return undefined as T;
  }

  const responseData = await response.json();

  if (!response.ok) {
    const errorMessage =
      responseData.error?.message || responseData.message || `API Error: ${response.status}`;
    throw new ApiError(errorMessage, response.status, responseData);
  }

  // Backend returns { data: T }, so extract the data field
  return responseData.data || responseData;
}

/**
 * Download a file from a REST endpoint as an authenticated browser download.
 *
 * A plain `<a href="/api/v1/...">` / `window.location.href` navigation cannot
 * be used for authenticated exports: the URL is relative (resolves against
 * the frontend origin, not the API) and carries no `Authorization` header
 * (auth is Bearer-token only — no session cookie), so the request 401s and no
 * `download` browser event ever fires (issue #598). Instead this fetches the
 * resource with the same auth header as `apiRequest`, reads the filename from
 * the server's `Content-Disposition` header, and triggers the download via a
 * short-lived object URL + anchor click — the same technique already used in
 * `lib/exportUtils.ts` for client-generated exports.
 */
export async function downloadFile(endpoint: string, fallbackFilename: string): Promise<void> {
  const url = `${API_BASE_URL}${endpoint}`;

  const token = store.getState().auth.token;
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url, { headers });

  if (!response.ok) {
    let responseData: unknown = {};
    try {
      responseData = await response.json();
    } catch {
      // Non-JSON error body (e.g. plain text) — fall back to a generic message below.
    }
    const parsed = responseData as { error?: { message?: string }; message?: string };
    const errorMessage = parsed.error?.message || parsed.message || `API Error: ${response.status}`;
    throw new ApiError(errorMessage, response.status, responseData);
  }

  const disposition = response.headers.get('Content-Disposition');
  const filenameMatch = disposition?.match(/filename="?([^";]+)"?/i);
  const filename = filenameMatch?.[1] ?? fallbackFilename;

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(objectUrl);
}

/**
 * Fetch the OpenAPI 3.1 document for the integration REST surface.
 *
 * Served behind auth at `/docs/openapi.json`; consumed by the in-app Swagger UI.
 * The endpoint returns the raw OpenAPI document (no `{ data }` envelope).
 */
export async function fetchOpenApiDocument(): Promise<Record<string, unknown>> {
  return apiRequest<Record<string, unknown>>('/docs/openapi.json', { method: 'GET' });
}
